// src/screens/close/AccumulationTimeline.tsx
// ─────────────────────────────────────────────────────────────────────────────
// S1 Close — Accumulation Timeline
// The full financial journey on one chart: pension/ISA/cash growing, mortgage
// falling, and the key milestones on the road to the Freedom Number.
// Brief 60E.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useMemo } from 'react';
import {
  ComposedChart, Area, Line, ReferenceLine,
  XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend,
} from 'recharts';
import { ClientData } from '../../types/ClientData';
import { POT_COLOURS } from '../../constants/potColours';
import { PENSION_ACCESS } from '../../taxConfig';

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// SIPP access age — pulls constants from taxConfig so that when 58 is
// legislated, the only file that changes is taxConfig.ts.
// ─────────────────────────────────────────────────────────────────────────────
const getSippAccessAge = (dateOfBirth?: string, currentAge?: number): number => {
  const { legacyAccessAge, legacyDeadline, post2028AccessAge } = PENSION_ACCESS;
  if (dateOfBirth) {
    const dob = new Date(dateOfBirth);
    const turnsLegacyAge = new Date(
      dob.getFullYear() + legacyAccessAge,
      dob.getMonth(),
      dob.getDate(),
    );
    return turnsLegacyAge < legacyDeadline ? legacyAccessAge : post2028AccessAge;
  }
  if (currentAge !== undefined) {
    const birthYear = new Date().getFullYear() - currentAge;
    const approxTurnsLegacy = birthYear + legacyAccessAge;
    return approxTurnsLegacy < legacyDeadline.getFullYear()
      ? legacyAccessAge
      : post2028AccessAge;
  }
  return post2028AccessAge; // Safe default
};

// Future value: lump + monthly contribution, annual rate, years.
function fv(balance: number, monthly: number, rate: number, years: number): number {
  if (years <= 0) return balance;
  const fvLump = balance * Math.pow(1 + rate, years);
  const fvMonthly = rate === 0
    ? monthly * 12 * years
    : monthly * 12 * (Math.pow(1 + rate, years) - 1) / rate;
  return fvLump + fvMonthly;
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

export default function AccumulationTimeline({ data }: Props) {
  const clientAge = data.clientAge || 40;
  const freedomAge = data.targetFreedomAge || 55;
  const clientName = data.clientName || 'Client';

  // ── Monthly contributions ────────────────────────────────────────────────
  const pensionMonthly =
    (data.pensionCompanyFunded || 0) + (data.planPensionFromDividendReduction || 0);
  const isaMonthly = data.planIsaMonthlyContribution || data.isaContribution || 0;
  const cashMonthly = 0;

  // ── Opening balances ─────────────────────────────────────────────────────
  const startPension = data.pensionBalance || 0;
  const startISA = data.investmentsBalance || 0;
  const startCash =
    (data.efAccount1Balance || 0) +
    (data.efAccount2Balance || 0) +
    (data.savingsAccounts || []).reduce(
      (s, a: { balance?: number }) => s + (a.balance || 0),
      0,
    );

  // ── Growth assumptions ───────────────────────────────────────────────────
  const pensionRate = data.assumptionPensionGrowth || 0.08;
  const isaRate = data.assumptionIsaGrowth || 0.08;
  const cashRate = data.assumptionCashRate || 0.01;

  // ── Mortgage ─────────────────────────────────────────────────────────────
  const mortgageStart = data.mortgageBalance || 0;
  const mortgageRate = (data.mortgageRate || 0) / 100;
  const mortgagePayment = data.mortgagePayment || 0;
  const mortgageOP = data.mortgageOP || 0;
  const mortgageIsIO = data.mortgageIsInterestOnly;
  const mortgageLumpPerYear = data.mortgageAnnualLumpSum || 0;

  // ── EF target ────────────────────────────────────────────────────────────
  const efTargetMonths = data.efTargetMonths || 3;
  const monthlyEssentials =
    (data.essentialsUtilities || 0) +
    (data.essentialsFood || 0) +
    (data.essentialsTravel || 0) +
    (data.mortgagePayment || 0);
  const efTarget = efTargetMonths * (monthlyEssentials > 0 ? monthlyEssentials : (data.livingCosts || 0));

  const freedomNumber = data.freedomNumberInflated || 0;
  const sippAccessAge = getSippAccessAge(undefined, clientAge);

  // ── Mortgage-free age (proper amortisation at fixed rate) ───────────────
  // Monthly interest = balance × (rate / 12)
  // Monthly capital  = scheduled payment − monthly interest
  // Years-to-free    = balance / (monthly capital × 12)
  // Note: uses the current balance's interest figure throughout — slightly
  // conservative because capital repayment accelerates as the balance falls,
  // but directionally accurate for a planning milestone. No hardcoded rates.
  // Reuses mortgageStart (= data.mortgageBalance), mortgageRate (already
  // converted to decimal on line 91) and mortgagePayment from the block
  // above, to avoid duplicate declarations.
  const monthlyInterest = mortgageStart * (mortgageRate / 12);
  const monthlyCapital = mortgagePayment - monthlyInterest;
  const yearsToMortgageFree = monthlyCapital > 0
    ? mortgageStart / (monthlyCapital * 12)
    : 999;
  const mortgageFreeAgeRaw = Math.round(clientAge + yearsToMortgageFree);
  const simpleMortgageFreeAge =
    mortgageStart > 0 && mortgageFreeAgeRaw <= clientAge + 80
      ? mortgageFreeAgeRaw
      : null;

  // ── Year-by-year projection ──────────────────────────────────────────────
  // Returns the milestones computed from the projection too.
  const projection = useMemo(() => {
    const rows: Array<{
      age: number;
      pension: number;
      isa: number;
      cash: number;
      total: number;
      mortgage: number;
    }> = [];

    let mortgage = mortgageStart;
    const endAge = freedomAge + 5;

    let mortgageFreeAge: number | null = null;
    let efCompleteAge: number | null = null;
    let coastReadyAge: number | null = null;
    let freedomCrossedAge: number | null = null;

    for (let age = clientAge; age <= endAge; age++) {
      const y = age - clientAge;
      const pension = fv(startPension, pensionMonthly, pensionRate, y);
      const isa = fv(startISA, isaMonthly, isaRate, y);
      const cash = fv(startCash, cashMonthly, cashRate, y);
      const total = pension + isa + cash;

      rows.push({
        age,
        pension: Math.round(pension),
        isa: Math.round(isa),
        cash: Math.round(cash),
        total: Math.round(total),
        mortgage: Math.round(Math.max(0, mortgage)),
      });

      // Detect milestones (first crossing)
      if (mortgageFreeAge === null && mortgage <= 0 && mortgageStart > 0) mortgageFreeAge = age;
      if (efCompleteAge === null && efTarget > 0 && cash >= efTarget) efCompleteAge = age;

      // Coast number at this age: pot that, left alone at pension growth, reaches
      // freedom number by freedomAge.
      const yrsToFreedom = freedomAge - age;
      const coastThreshold = yrsToFreedom > 0
        ? freedomNumber / Math.pow(1 + pensionRate, yrsToFreedom)
        : freedomNumber;
      if (coastReadyAge === null && total >= coastThreshold && freedomNumber > 0 && age <= freedomAge) {
        coastReadyAge = age;
      }

      if (freedomCrossedAge === null && total >= freedomNumber && freedomNumber > 0) {
        freedomCrossedAge = age;
      }

      // Advance mortgage for next year
      const interest = mortgage * mortgageRate;
      const principal = mortgageIsIO
        ? mortgageOP * 12
        : Math.max(0, (mortgagePayment + mortgageOP) * 12 - interest);
      mortgage = Math.max(0, mortgage - principal - mortgageLumpPerYear);
    }

    return { rows, mortgageFreeAge, efCompleteAge, coastReadyAge, freedomCrossedAge };
  }, [
    clientAge, freedomAge, startPension, startISA, startCash,
    pensionMonthly, isaMonthly, cashMonthly,
    pensionRate, isaRate, cashRate,
    mortgageStart, mortgageRate, mortgagePayment, mortgageOP, mortgageIsIO,
    mortgageLumpPerYear, efTarget, freedomNumber,
  ]);

  const { rows: chartData, efCompleteAge, coastReadyAge, freedomCrossedAge } = projection;
  // Use the simple formula for the headline milestone, not the loop-derived one.
  const mortgageFreeAge = simpleMortgageFreeAge;

  // Formatters
  const fmt = (n: number) => `£${Math.round(n).toLocaleString()}`;
  const fmtK = (n: number) => `£${Math.round(n / 1000)}k`;
  const labelAge = (a: number | null) => (a === null ? '—' : `Age ${a}`);

  // ── RENDER ───────────────────────────────────────────────────────────────
  return (
    <div className="max-w-5xl mx-auto py-8 px-6">
      {/* HEADER */}
      <h2 className="text-xl font-bold text-ma-navy mb-2">Accumulation Timeline</h2>
      <p className="text-sm text-gray-600 mb-8">
        {clientName}'s full financial journey — all pots growing, mortgage falling,
        milestones marked — on one chart.
      </p>

      {/* MILESTONE STAT CARDS */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="rounded-2xl border border-gray-200 bg-white p-5">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
            Mortgage-free
          </div>
          <div className="text-3xl font-bold text-ma-navy">{labelAge(mortgageFreeAge)}</div>
          <p className="text-xs text-gray-500 mt-2">
            When scheduled payments{mortgageOP > 0 ? ' + overpayments' : ''} clear
            the balance.
          </p>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-5">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
            Pension unlocks
          </div>
          <div className="text-3xl font-bold text-ma-navy">Age {sippAccessAge}</div>
          <p className="text-xs text-gray-500 mt-2">
            SIPP access age under current rules (Finance Act 2022).
          </p>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-5">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
            Freedom Number crossed
          </div>
          <div className="text-3xl font-bold text-ma-navy">
            {freedomCrossedAge === null
              ? <span className="text-base font-semibold text-gray-500">Not reached on current plan</span>
              : labelAge(freedomCrossedAge)}
          </div>
          <p className="text-xs text-gray-500 mt-2">
            When combined pot hits {fmt(freedomNumber)}.
          </p>
        </div>
      </div>

      {/* CHART */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5 mb-6">
        <h3 className="text-sm font-bold text-ma-navy uppercase tracking-wide mb-3">
          Pots, mortgage and milestones
        </h3>
        <div style={{ width: '100%', height: 360 }}>
          <ResponsiveContainer>
            <ComposedChart data={chartData} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="age" tick={{ fontSize: 11 }} stroke="#9CA3AF" />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtK} stroke="#9CA3AF" />
              <Tooltip
                formatter={(v: any) => fmt(Number(v))}
                labelFormatter={(age) => `Age ${age}`}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {/* Stacked pots */}
              <Area
                type="monotone"
                dataKey="pension"
                name="Pension"
                stackId="pots"
                fill={POT_COLOURS.pension}
                fillOpacity={0.85}
                stroke={POT_COLOURS.pension}
                strokeWidth={1}
              />
              <Area
                type="monotone"
                dataKey="isa"
                name="ISA / Investments"
                stackId="pots"
                fill={POT_COLOURS.investments}
                fillOpacity={0.85}
                stroke={POT_COLOURS.investments}
                strokeWidth={1}
              />
              <Area
                type="monotone"
                dataKey="cash"
                name="Cash"
                stackId="pots"
                fill={POT_COLOURS.cash}
                fillOpacity={0.85}
                stroke={POT_COLOURS.cash}
                strokeWidth={1}
              />
              {/* Mortgage line (liability — separate axis of meaning) */}
              <Line
                type="monotone"
                dataKey="mortgage"
                name="Mortgage"
                stroke={POT_COLOURS.mortgage}
                strokeWidth={2}
                dot={false}
              />
              {/* Freedom Number */}
              {freedomNumber > 0 && (
                <ReferenceLine
                  y={freedomNumber}
                  stroke="#C9A84C"
                  strokeDasharray="4 4"
                  label={{ value: `Freedom Number ${fmtK(freedomNumber)}`, fontSize: 10, fill: '#C9A84C', position: 'insideTopLeft' }}
                />
              )}
              {/* Milestone vertical lines */}
              {efCompleteAge !== null && (
                <ReferenceLine
                  x={efCompleteAge}
                  stroke="#60A5FA"
                  strokeDasharray="2 4"
                  label={{ value: `EF @ ${efCompleteAge}`, fontSize: 10, fill: '#60A5FA', position: 'top' }}
                />
              )}
              {mortgageFreeAge !== null && (
                <ReferenceLine
                  x={mortgageFreeAge}
                  stroke={POT_COLOURS.mortgage}
                  strokeDasharray="2 4"
                  label={{ value: `Mortgage-free @ ${mortgageFreeAge}`, fontSize: 10, fill: POT_COLOURS.mortgage, position: 'top' }}
                />
              )}
              {coastReadyAge !== null && (
                <ReferenceLine
                  x={coastReadyAge}
                  stroke="#10B981"
                  strokeDasharray="2 4"
                  label={{ value: `Coast @ ${coastReadyAge}`, fontSize: 10, fill: '#10B981', position: 'top' }}
                />
              )}
              <ReferenceLine
                x={sippAccessAge}
                stroke="#9CA3AF"
                strokeDasharray="2 4"
                label={{ value: `SIPP @ ${sippAccessAge}`, fontSize: 10, fill: '#9CA3AF', position: 'top' }}
              />
              <ReferenceLine
                x={freedomAge}
                stroke="#1E3A5F"
                strokeDasharray="3 3"
                label={{ value: `Freedom @ ${freedomAge}`, fontSize: 11, fill: '#1E3A5F', position: 'top' }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* SECONDARY MILESTONE CARDS */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="rounded-2xl border border-gray-200 bg-gray-50 p-5">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
            EF complete
          </div>
          <div className="text-2xl font-bold text-ma-navy">{labelAge(efCompleteAge)}</div>
          <p className="text-xs text-gray-500 mt-2">
            {efTarget > 0
              ? `When cash pot reaches ${fmt(efTarget)} (${efTargetMonths} months of essentials).`
              : 'No EF target set — complete the EF lever to calculate.'}
          </p>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-gray-50 p-5">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
            Coast ready
          </div>
          <div className="text-2xl font-bold text-ma-navy">{labelAge(coastReadyAge)}</div>
          <p className="text-xs text-gray-500 mt-2">
            The point from which, even with zero further contributions, the pot
            grows to the Freedom Number by age {freedomAge}.
          </p>
        </div>
      </div>

      {/* PLAIN ENGLISH */}
      <div className="bg-ma-navy text-white rounded-2xl p-6 mb-6">
        <p className="text-sm leading-relaxed">
          From today through age {freedomAge}, here's the picture: the pension,
          ISA and cash pots grow together; the mortgage line falls toward zero.
          {mortgageFreeAge !== null && (
            <> You're mortgage-free at <strong>{mortgageFreeAge}</strong>.</>
          )}
          {' '}Your SIPP unlocks at <strong>{sippAccessAge}</strong>.
          {coastReadyAge !== null && (
            <> From age <strong>{coastReadyAge}</strong>, even with zero further
            contributions, growth alone carries you to the Freedom Number.</>
          )}
          {freedomCrossedAge !== null ? (
            <> The combined pot crosses your Freedom Number of
            <strong> {fmt(freedomNumber)}</strong> at age
            <strong> {freedomCrossedAge}</strong>.</>
          ) : (
            <> On the current plan, the combined pot doesn't cross the Freedom
            Number within the timeline shown — revisit contributions or freedom age.</>
          )}
        </p>
      </div>
    </div>
  );
}
